import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Marquee } from '@sorare/core/src/atoms/container/Marquee';
import { SorareLogo } from '@sorare/core/src/atoms/icons/SorareLogo';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayM } from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { BLOG_POST } from '@sorare/blog/src/constants/routes';

import boeBgAvif from './assets/boe-bg.avif';
import boeBg from './assets/boe-bg.jpg';
import boeMobileAvif from './assets/boe-mobile.avif';
import boeMobile from './assets/boe-mobile.jpg';
import boeTitleAvif from './assets/boe-title.avif';
import boeTitle from './assets/boe-title.png';
import ribonBackAvif from './assets/ribon-back.avif';
import ribonBack from './assets/ribon-back.jpg';
import ribonAvif from './assets/ribon.avif';
import ribon from './assets/ribon.jpg';

const Root = styled.section`
  position: relative;
  isolation: isolate;
`;
const Grid = styled.div`
  position: relative;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  padding: var(--quadruple-unit) 0 0;
  background-image: url(${boeMobile}});
  background-image: image-set(
    url(${boeMobileAvif}) type('image/avif'),
    url(${boeMobile})
  );
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  margin-bottom: var(--double-unit);
  width: 100%;
  aspect-ratio: 750 / 2026;
  @media ${laptopAndAbove} {
    justify-content: center;
    gap: var(--double-unit);
    margin-bottom: 0;
    background-image: url(${boeBg});
    background-image: image-set(
      url(${boeBgAvif}) type('image/avif'),
      url(${boeBg})
    );
    aspect-ratio: 2880 / 1620;
  }
`;
const MarqueeContent = styled(Horizontal).attrs({ gap: 0 })`
  height: 50px;
`;

const Content = styled(Vertical)`
  grid-area: content;
  align-items: center;
  z-index: 1;
  text-align: center;
`;
const Ribbon = styled.div`
  position: relative;
  z-index: 2;
  font-family: var(--rigid-square);
  font-size: 43px;
  letter-spacing: 0.08em;
  font-weight: bold;
  transform: rotate(-0.7deg);
  text-transform: uppercase;
  &::before,
  &::after {
    position: absolute;
    inset: 0;
    z-index: -1;
    content: '';
  }
  &:before {
    transform: rotate(-1deg);
    background-image: url(${ribonBack}});
    background-image: image-set(
      url(${ribonBackAvif}) type('image/avif'),
      url(${ribonBack})
    );
  }
  &:after {
    background-image: image-set(
      url(${ribonAvif}) type('image/avif'),
      url(${ribon})
    );
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: var(--double-unit);
  justify-content: center;
  @media ${laptopAndAbove} {
    justify-content: start;
    flex-direction: row;
  }
`;

export const BestOfEuropeBanner = () => {
  const { signUp } = useConnectionContext();
  const isLaptop = useIsLaptop();

  return (
    <Root>
      <Ribbon>
        <Marquee direction="reverse" duration="10s">
          <MarqueeContent>
            <SorareLogo />
          </MarqueeContent>
        </Marquee>
      </Ribbon>
      <Grid>
        <Content gap={3}>
          {isLaptop && (
            <picture>
              <source srcSet={boeTitleAvif} type="image/avif" />
              <Img src={boeTitle} alt="" width="680" height="275" />
            </picture>
          )}
          <DisplayM>
            <FormattedMessage
              id="landing.EarlyAccess.BestOfEuropeBanner.desc"
              defaultMessage="The Best of Europe Competitions"
            />
          </DisplayM>
          <Buttons>
            <Button color="secondary" size="large" onClick={signUp}>
              <FormattedMessage
                id="landing.EarlyAccess.BestOfEuropeBanner.enter"
                defaultMessage="Enter competition"
              />
            </Button>
            <Button
              color="transparent"
              size="large"
              to={generatePath(BLOG_POST, {
                sport: 'football',
                slug: 'introducing-best-of-europe-competitions-play-for-amazing-prizes-every-game-week',
              })}
            >
              <FormattedMessage
                id="landing.EarlyAccess.BestOfEuropeBanner.learn"
                defaultMessage="Learn more"
              />
            </Button>
          </Buttons>
        </Content>
      </Grid>
    </Root>
  );
};
